import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { Plus, Trash2, Edit, X, Upload, TrendingUp, Star, IndianRupee, Save, AlertTriangle } from 'lucide-react';
import { getFromLocalStorage } from '../utils/localStorage';

const TrendingCardsManagement = () => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCard, setEditingCard] = useState(null);
  const [formData, setFormData] = useState({
    cardTitle: '',
    cardDescription: '',
    price: '',
    rarity: 'Common',
    cardImage: '',
    category: 'Featured',
    isTrending: true
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [cardToDelete, setCardToDelete] = useState(null);
  const fetchedRef = useRef(false);

  const rarityOptions = ['Common', 'Rare', 'Epic', 'Legendary', 'Mythic'];

  const fetchCards = useCallback(async () => {
    if (fetchedRef.current) return;

    setLoading(true);
    try {
      const adminToken = getFromLocalStorage("adminToken");

      if (!adminToken) {
        setCards([]);
        setLoading(false);
        return;
      }

      const resp = await fetch(`${process.env.REACT_APP_API_BASE_URL}/auth/user/getTrendingCards`, {
        headers: {
          Authorization: `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (resp.ok) {
        const data = await resp.json();
        const cards = data.cards || data.data || [];
        setCards(Array.isArray(cards) ? cards : []);
      } else {
        console.error('Failed to fetch trending cards:', resp.status, resp.statusText);
        setCards([]);
      }
      fetchedRef.current = true;
    } catch (error) {
      console.error('Error fetching trending cards:', error);
      setCards([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCards();
  }, [fetchCards]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
        setFormData(prev => ({ ...prev, cardImage: e.target.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const token = getFromLocalStorage("adminToken");
      const cardData = {
        cardTitle: formData.cardTitle,
        description: formData.cardDescription,
        price: parseFloat(formData.price),
        rarity: formData.rarity,
        imageUrl: formData.cardImage,
        category: formData.category || 'Featured',
        duration: 10,
        isTrending: true
      };

      let ok = false;
      if (editingCard) {
        const resp = await fetch(`${process.env.REACT_APP_API_BASE_URL}/auth/admin/trending-cards/${editingCard._id}`, {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            cardTitle: cardData.cardTitle,
            description: cardData.description,
            imageUrl: cardData.imageUrl,
            category: cardData.category,
            price: cardData.price,
            rarity: cardData.rarity
          })
        });
        ok = resp.ok;
        if (ok) {
          toast.success('Trending card updated successfully!');
          // Update the card in local state
          setCards(prevCards => prevCards.map(card =>
            card._id === editingCard._id
              ? { ...card, ...cardData, description: cardData.description, cardDescription: cardData.description }
              : card
          ));
        }
      } else {
        const resp = await fetch(`${process.env.REACT_APP_API_BASE_URL}/auth/admin/trending-cards`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            cardTitle: cardData.cardTitle,
            description: cardData.description,
            imageUrl: cardData.imageUrl,
            category: cardData.category,
            price: cardData.price,
            duration: cardData.duration,
            rarity: cardData.rarity,
            isTrending: true
          })
        });
        ok = resp.ok;
        if (ok) toast.success('Trending card created successfully!');
      }

      if (ok) {
        setShowModal(false);
        setEditingCard(null);
        setFormData(prev => ({ ...prev, cardTitle: '', cardDescription: '', price: '', rarity: 'Common', cardImage: '' }));
        setImagePreview('');
        fetchedRef.current = false;
        fetchCards();
      } else {
        toast.error('Failed to save trending card');
      }
    } catch (error) {
      console.error('Error saving trending card:', error);
      toast.error('Failed to save trending card');
    }
  };

  const handleEdit = (card) => {
    setEditingCard(card);
    setFormData({
      cardTitle: card.cardTitle || '',
      cardDescription: card.cardDescription || card.description || '',
      price: card.price || '',
      rarity: card.rarity || 'Common',
      category: Array.isArray(card.category) ? card.category : [card.category || ''],
      cardImage: card.cardImage || card.imageUrl || '',
      isTrending: true
    });
    setImagePreview(card.cardImage || card.imageUrl || '');
    setShowModal(true);
  };

  const confirmDeleteCard = (cardId) => {
    setCardToDelete(cardId);
    setShowDeleteModal(true);
  };

  const handleDeleteCard = async () => {
    console.log('handleDeleteCard called with cardId:', cardToDelete);
    console.log('Proceeding with deletion...');

    try {
      const token = getFromLocalStorage("adminToken");
      console.log('Got admin token:', token ? 'Token exists' : 'No token');

      const resp = await fetch(`${process.env.REACT_APP_API_BASE_URL}/auth/admin/trending-cards/${cardToDelete}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Delete request response status:', resp.status);
      console.log('Delete request response ok:', resp.ok);

      if (resp.ok) {
        setCards(prev => prev.filter(card => card._id !== cardToDelete));
        toast.success('Trending card deleted successfully!');
        setShowDeleteModal(false);
        setCardToDelete(null);
      } else {
        toast.error('Failed to delete trending card');
        setShowDeleteModal(false);
        setCardToDelete(null);
      }
    } catch (error) {
      console.error('Error deleting trending card:', error);
      toast.error('Error deleting trending card');
      setShowDeleteModal(false);
      setCardToDelete(null);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingCard(null);
    setFormData({
      cardTitle: '',
      cardDescription: '',
      price: '',
      rarity: 'Common',
      cardImage: '',
      category: 'Featured',
      isTrending: true
    });
    setImageFile(null);
    setImagePreview('');
  };


  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white">
      <main className="pt-6 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-black" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold bg-gradient-to-r from-yellow-400 to-yellow-600 bg-clip-text text-transparent">
                    Trending Cards Management
                  </h1>
                  <p className="text-gray-400">Manage trending cards shown on the Dashboard</p>
                </div>
              </div>
              <button
                onClick={() => setShowModal(true)}
                className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-black px-6 py-3 rounded-lg font-semibold hover:from-yellow-500 hover:to-yellow-700 transition-colors flex items-center space-x-2"
              >
                <Plus className="w-5 h-5" />
                <span>Add Trending Card</span>
              </button>
            </div>
          </div>

          {/* Cards Grid */}
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {[...Array(8)].map((_, index) => (
                <div key={index} className="bg-black/30 rounded-xl p-6 border border-gray-800 backdrop-blur-sm animate-pulse">
                  <div className="w-full h-48 bg-gray-700 rounded-lg mb-4"></div>
                  <div className="h-6 bg-gray-700 rounded mb-2"></div>
                  <div className="h-4 bg-gray-700 rounded mb-4"></div>
                  <div className="flex space-x-2">
                    <div className="h-8 bg-gray-700 rounded flex-1"></div>
                    <div className="h-8 bg-gray-700 rounded flex-1"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {cards.map((card) => (
                <div key={card._id} className="bg-black/30 rounded-xl p-6 border border-gray-800 backdrop-blur-sm hover:border-yellow-500/50 transition-all duration-300">
                  <div className="relative mb-4 overflow-hidden rounded-lg">
                    <img
                      src={card.imageUrl || card.cardImage || "https://picsum.photos/300/200"}
                      alt={card.cardTitle}
                      className="object-contain bg-black/50"
                      style={{ width: '100%', height: '300px' }}
                      onError={(e) => {
                        e.target.src = "https://picsum.photos/300/200";
                      }}
                    />
                    <div className="absolute top-2 left-2 bg-yellow-400 text-black px-2 py-1 rounded-full text-xs font-bold">
                      TRENDING
                    </div>
                  </div>

                  <div className="space-y-3">
                    <h3 className="text-lg font-bold text-white line-clamp-2">
                      {card.cardTitle}
                    </h3>

                    <p className="text-gray-400 text-sm line-clamp-2">
                      {card.description || card.cardDescription}
                    </p>

                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center space-x-2">
                        <Star className="w-4 h-4 text-yellow-400" />
                        <span className="text-gray-400">{card.rarity}</span>
                      </div>
                      <div className="flex items-center space-x-1 text-yellow-400">
                        <IndianRupee className="w-4 h-4" />
                        <span>{card.price ?? card.cardPrice}</span>
                      </div>
                    </div>

                    {/* Categories removed as requested */}

                    <div className="flex space-x-2 mt-4">
                      <button
                        onClick={() => handleEdit(card)}
                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-3 rounded-lg transition-colors flex items-center justify-center space-x-1"
                      >
                        <Edit className="w-4 h-4" />
                        <span>Edit</span>
                      </button>
                      <button
                        onClick={() => {
                          console.log('Delete button clicked on TrendingCardsManagement');
                          console.log('Card ID:', card._id);
                          console.log('Card object:', card);
                          confirmDeleteCard(card._id);
                        }}
                        className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 px-3 rounded-lg transition-colors flex items-center justify-center space-x-1"
                      >
                        <Trash2 className="w-4 h-4" />
                        <span>Delete</span>
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              {cards.length === 0 && !loading && (
                <div className="col-span-full text-center py-20">
                  <div className="w-24 h-24 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                    <TrendingUp className="w-12 h-12 text-gray-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-400 mb-2">No Trending Cards</h3>
                  <p className="text-gray-500 mb-6">Create your first trending card to get started!</p>
                  <button
                    onClick={() => setShowModal(true)}
                    className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-black px-6 py-3 rounded-lg font-semibold hover:from-yellow-500 hover:to-yellow-700 transition-colors flex items-center space-x-2 mx-auto"
                  >
                    <Plus className="w-5 h-5" />
                    <span>Add First Trending Card</span>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-black/90 backdrop-blur-xl border border-gray-700 rounded-xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">
                {editingCard ? 'Edit Trending Card' : 'Add New Trending Card'}
              </h2>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Image Upload */}
              <div>
                <label className="block text-sm font-semibold text-yellow-400 mb-2">
                  Card Image
                </label>
                <div className="relative">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                    id="image-upload"
                  />
                  <label
                    htmlFor="image-upload"
                    className="w-full h-[300px] bg-gray-800/50 border-2 border-dashed border-gray-600 rounded-lg cursor-pointer hover:border-yellow-500/50 transition-all flex items-center justify-center overflow-hidden"
                  >
                    {imagePreview ? (
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="object-contain"
                        style={{ width: '100%', height: '300px' }}
                      />
                    ) : (
                      <div className="text-center">
                        <Upload className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                        <p className="text-gray-400">Click to upload image</p>
                      </div>
                    )}
                  </label>
                </div>
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-semibold text-yellow-400 mb-2">
                  Card Title
                </label>
                <input
                  type="text"
                  value={formData.cardTitle}
                  onChange={(e) => setFormData(prev => ({ ...prev, cardTitle: e.target.value }))}
                  className="w-full px-4 py-3 bg-black/30 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-yellow-500 transition-colors"
                  placeholder="Enter card title..."
                  required
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-semibold text-yellow-400 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.cardDescription}
                  onChange={(e) => setFormData(prev => ({ ...prev, cardDescription: e.target.value }))}
                  rows={4}
                  className="w-full px-4 py-3 bg-black/30 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-yellow-500 transition-colors resize-none"
                  placeholder="Enter card description..."
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Price */}
                <div>
                  <label className="block text-sm font-semibold text-yellow-400 mb-2">
                    Price (₹)
                  </label>
                  <input
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                    className="w-full px-4 py-3 bg-black/30 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-yellow-500 transition-colors"
                    placeholder="Enter price..."
                    min="0"
                    step="0.01"
                    required
                  />
                </div>

                {/* Rarity */}
                <div>
                  <label className="block text-sm font-semibold text-yellow-400 mb-2">
                    Rarity
                  </label>
                  <select
                    value={formData.rarity}
                    onChange={(e) => setFormData(prev => ({ ...prev, rarity: e.target.value }))}
                    className="w-full px-4 py-3 bg-black/30 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500 transition-colors"
                  >
                    {rarityOptions.map(rarity => (
                      <option key={rarity} value={rarity}>{rarity}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Categories removed */}

              {/* Submit Buttons */}
              <div className="flex space-x-4 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-semibold py-3 px-4 rounded-lg hover:from-yellow-500 hover:to-yellow-700 transition-all duration-200 flex items-center justify-center space-x-2"
                >
                  <Save className="w-4 h-4" />
                  <span>{editingCard ? 'Update Card' : 'Create Card'}</span>
                </button>
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="flex-1 bg-gray-700 hover:bg-gray-600 text-white font-semibold py-3 px-4 rounded-lg transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex justify-center items-center z-50 p-4">
          <div className="bg-black/90 rounded-xl p-8 border border-gray-700 w-full max-w-md mx-auto shadow-2xl">
            <div className="text-center">
              <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <AlertTriangle className="w-10 h-10 text-red-400" />
              </div>
              <h2 className="text-2xl font-bold mb-4 text-white">Confirm Deletion</h2>
              <p className="text-gray-300 mb-8 text-lg">Are you sure you want to delete this trending card? This action cannot be undone.</p>
              <div className="flex justify-center space-x-4">
                <button
                  onClick={handleDeleteCard}
                  className="bg-red-600 hover:bg-red-700 px-8 py-3 rounded-lg font-semibold transition-all duration-200 flex items-center space-x-2 text-white shadow-lg hover:shadow-red-500/25"
                >
                  <Trash2 className="w-5 h-5" />
                  <span>Yes, Delete</span>
                </button>
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setCardToDelete(null);
                  }}
                  className="bg-gray-600 hover:bg-gray-700 px-8 py-3 rounded-lg font-semibold transition-all duration-200 text-white shadow-lg"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrendingCardsManagement;
